import { EarningRuleGlobalLimitReward, recordListUserInfo } from 'config/CustomEnums';
import {
  getStampRecord,
  getStampRecords,
} from '../services/StampRecordsApiHelper';
import {
  convertCursorToNumber,
  convertNumberToCursor,
  createAction,
  formatNumberWithCommas,
} from '../utils';
import { getDetailPageDisplayLastModifiedDate, getListDisplayLastModifiedDate } from '../utils/TimeFormatUtil';
import { createModel } from './BaseModel';
import { loading } from './LoadingUtil';
import { parseTransactionDisplayType, transactionDisplayInvoiceIdAndId } from './TransactionModel';
import { 
  getDisplayQuantityWithLimitInfo,
  getCampaignDisplayName,
  getCampaignLink,
} from 'models/RecordUtils';
import { parseReachLimitInfo } from 'utils/ReachLimitInfoUtils';

const eventType = {
  ADD_STAMP: 'Add Stamp',
  REMOVE_STAMP: 'Remove Stamp',
};
export const recordType = {
  CUSTOMER_EARN: 'Customer Earn',
  ADMIN_REMOVE: 'Admin Remove',
  ADMIN_ADD: 'Admin Add',
  MERCHANT_REMOVE: 'Merchant Remove',
  MERCHANT_ADD: 'Merchant Add',
  STAMP_EXPIRED: 'Stamp Expired',
  ADMIN_TRANSFERRED: 'Admin transferred',
};
function getDisplayQuantity(item) {
  return getDisplayQuantityWithLimitInfo(item, {
    rewardedQuantityField: 'stampQuantity',
    typeField: 'eventType',
    addTypeValue: 'ADD_STAMP',
    initialValueKeyInJson: 'stamps_value',
    unit: 'stamp',
    limitTypeValue: 'STAMPS'
  });
}

function getDisplayTaskName(earningRule) {
  if(!earningRule?.pk) {
    return "-"
  }
  return earningRule.name
}

function getInitState() {
  return {
    listDisplayFields: [
      { displayName: 'ID', fieldName: 'pk', linked: true },
      ...recordListUserInfo,
      { displayName: 'Event Type', fieldName: 'displayEventType' },
      {
        displayName: 'Quantity',
        fieldName: 'quantity',
        orderField: 'quantity',
      },
      { displayName: 'Campaign', fieldName: 'displayCampaignInfo' },
      { displayName: 'Task Name', fieldName: 'displayTaskNameInfo' },
      // { displayName: 'Remark', fieldName: 'remark' },
      {
        displayName: 'Purchase date',
        fieldName: 'listDisplayPurchaseDate',
        orderField: 'triggerTime',
      },
      {
        displayName: 'Creation date',
        fieldName: 'listDisplayCreationDate',
        orderField: 'creationDate',
      },
    ],
    maxQuantityValue: 0,
  };
}

const parseStampRecordLinkedTransaction = (transaction) => {
  if (!transaction) {
    return {};
  }
  const transactionType = transaction?.transactionType
  return {
    pk: transaction?.pk,
    transactionType,
    transactionDisplayType: parseTransactionDisplayType(transaction.transactionType),
    posInvoiceId: transaction?.posInvoiceId,
    invoiceId: transaction?.invoiceId,
    totalValue: transaction.totalValue,
    displayTotalValue: transaction.totalValue
      ? `HK$ ${formatNumberWithCommas(transaction.totalValue)}`
      : '',
    paidAmount: transaction.paidAmount,
    displayPaidAmount: transaction.paidAmount
      ? `HK$ ${formatNumberWithCommas(transaction.paidAmount)}`
      : '',
    transactionDetailDisplayDate: getDetailPageDisplayLastModifiedDate(
      transaction.date
    ),
    transactionDisplayInvoiceInfo: transactionDisplayInvoiceIdAndId(transaction),
    detailDisplayPurchaseDate: getDetailPageDisplayLastModifiedDate(
      transaction.purchasedDate
    ),
    purchaseDate: transaction.purchaseDate
  }
}


function parseStampRecord(item) {
  const name = item.owner?.nickname
    ? item.owner.nickname
    : `${item.owner.firstName} ${item.owner.lastName}`;
  return {
    pk: item.pk,
    id: item.id,
    name: name,
    membershipId: item.owner?.membershipId,
    mobilePhoneNumber: item.owner?.mobilePhoneNumberSubscriberNumber
      ? `+${item.owner.mobilePhoneNumberCountryCode}\r\n${item.owner.mobilePhoneNumberSubscriberNumber}` : null,
    email: item.owner?.emailAddress,
    customerId: item.owner?.pk,
    customerIsRestricted: item.owner?.isReset || item.owner?.isTransferred,
    recordType: item.recordType,
    displayRecordType: recordType[item.recordType],
    eventType: item.eventType,
    displayEventType: eventType[item.eventType],
    quantity: item.stampQuantity === 0 ? "0" : item.stampQuantity,
    remark: item.remark,
    listDisplayCreationDate: getListDisplayLastModifiedDate(item.creationDate),
    detailDisplayCreationDate: getDetailPageDisplayLastModifiedDate(item.creationDate),
    listDisplayPurchaseDate: getListDisplayLastModifiedDate(item.triggerTime),
    detailDisplayPurchaseDate: getDetailPageDisplayLastModifiedDate(item.triggerTime),
    transaction: parseStampRecordLinkedTransaction(item.transaction),
    reachLimit: item.reachLimit,
    reachLimitInfo: parseReachLimitInfo(
      item.reachLimitInfo,
      item.allReachLimitInfo,
      EarningRuleGlobalLimitReward.STAMPS
    ),
    displayAdministrator: item.administrator?.pk
      ? `[ID: ${item.administrator?.pk}] ${item.administrator?.username}`
      : item.administrator?.username,
    displayQuantity: getDisplayQuantity(item),
    displayCampaignInfo: getCampaignDisplayName(item?.campaign),
    campaignLink: getCampaignLink(item?.campaign),
    campaign: item?.campaign,
    earningRule: item?.earningRule,
    displayTaskNameInfo: getDisplayTaskName(item?.earningRule),
  };
}

export default createModel({
  namespace: 'stampRecord',
  states: getInitState(),
  params: {
    objectKey: 'stampRecords',
    pkNode: 'StampRecordNode',
    detailAPI: getStampRecord,
    parseDetail: (data) => parseStampRecord(data?.stampRecord),
    initState: getInitState(),
  },
  reducers: {},
  effects: {
    getCurrentPageStampRecords: [
      function* ({ payload }, { call, put }) {
        const { page } = payload;
        let afterCursor = undefined;
        if (page > 1) {
          afterCursor = convertNumberToCursor((page - 1) * 20 - 1);
        }
        const serviceArgs = [getStampRecords, afterCursor, payload];
        function* onSuccess(data) {
          const stampRecordData = data.stampRecords?.edges;
          const pageInfo = data.stampRecords?.pageInfo;
          const totalCount = data.stampRecords.totalCount;
          const startCursor = convertCursorToNumber(pageInfo.startCursor);
          const endCursor = convertCursorToNumber(pageInfo.endCursor);
          const stampRecordDataList = stampRecordData.map((item) =>
            parseStampRecord(item.node),
          );
          yield put(
            createAction('updateState')({
              pagedList: stampRecordDataList,
              pageInfo: {
                startCursor: isNaN(startCursor) ? 0 : startCursor + 1,
                endCursor: isNaN(endCursor) ? 0 : endCursor + 1,
              },
              totalCount,
              totalPage: Math.ceil(totalCount / 20),
              maxQuantityValue: data.stampRecords.maxStampQuantity,
            }),
          );
        }
        yield loading(serviceArgs, onSuccess);
      },
      { type: 'takeLatest' },
    ],
  },
});
